import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorNotStackable - The not-stackable error element.
 * @property {HTMLElement} cartDiscountReplaced - The replacement success message element.
 * @property {HTMLElement} cartDiscountAdded - The additional discount added success message element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorNotStackable', 'cartDiscountReplaced', 'cartDiscountAdded'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #replacedMessageHideAt = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #addedMessageHideAt = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorNotStackable, cartDiscountReplaced } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      cartDiscountErrorNotStackable.classList.add('hidden');
      cartDiscountReplaced.classList.add('hidden');
      this.#hideAddedMessage();

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      console.log('[Cart discount] API response discount_codes:', data.discount_codes, 'existing:', existingDiscounts, 'new:', discountCodeValue);

      if (
        data.discount_codes &&
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        if (existingDiscounts.length > 0) {
          const existingCode = existingDiscounts[0];
          if (typeof existingCode !== 'string') {
            this.#handleNotStackableError(existingDiscounts);
            void this.#persistDiscountCodes(existingDiscounts);
            return;
          }
          const replaceResult = await this.#tryReplaceDiscount(
            data,
            discountCodeValue,
            existingDiscounts,
            abortController,
          );
          if (replaceResult === 'replaced') return;
          if (replaceResult === 'not_better') {
            this.#handleNotStackableError(existingDiscounts);
            void this.#persistDiscountCodes(existingDiscounts);
            return;
          }
          this.#handleDiscountError('discount_code');
          void this.#persistDiscountCodes(existingDiscounts);
        } else {
          this.#handleDiscountError('discount_code');
          void this.#persistDiscountCodes([]);
        }
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      const sectionId = this.dataset.sectionId;

      const pillCodes = Array.from(discountCodes)
        .map((el) => (el instanceof HTMLLIElement ? el.dataset.discountCode : null))
        .filter(Boolean);

      const activeCodes = (data.discount_codes || [])
        .filter((/** @type {{ code: string; applicable: boolean }} */ d) => d.applicable === true)
        .map((/** @type {{ code: string }} */ d) => d.code);

      const effectiveCodes = pillCodes.length > 0 ? pillCodes : activeCodes;
      const newCodeLower = discountCodeValue.toLowerCase();
      const effectiveLower = effectiveCodes.map((/** @type {string} */ c) => c.toLowerCase());
      const newCodeInEffective = effectiveLower.includes(newCodeLower);

      const replacedCodes = existingDiscounts.filter((code) => !effectiveLower.includes(code.toLowerCase()));

      const wasReplacement =
        existingDiscounts.length > 0 &&
        newCodeInEffective &&
        replacedCodes.length > 0;

      const wasStacked =
        existingDiscounts.length > 0 &&
        newCodeInEffective &&
        replacedCodes.length === 0;

      console.log('[Cart discount] Detection:', { existingDiscounts, pillCodes, activeCodes, effectiveCodes, replacedCodes, wasReplacement, wasStacked });

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(sectionId, newHtml);

      if (wasReplacement) {
        const replacedLabel = replacedCodes.join(', ');
        setTimeout(() => this.#showReplacedMessage(replacedLabel, discountCodeValue, replacedCodes.length), 1500);
        console.log('[Cart discount] Replaced:', { previous: replacedLabel, new: discountCodeValue });
      } else if (wasStacked) {
        setTimeout(() => this.#showAddedMessage(discountCodeValue), 1500);
        console.log('[Cart discount] Stacked:', discountCodeValue);
      } else {
        setTimeout(() => this.#showAddedMessage(discountCodeValue, 'Your discount has been applied.'), 1500);
        console.log('[Cart discount] Applied:', discountCodeValue);
      }
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Shows the not-stackable error with the existing discount code name.
   * @param {string[]} existingDiscounts - Currently applied discount codes.
   */
  #handleNotStackableError(existingDiscounts) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorNotStackable } = this.refs;
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    const template = cartDiscountErrorNotStackable.dataset.messageTemplate ?? '';
    const codeLabel = (existingDiscounts.length > 0 ? existingDiscounts[0] : '') ?? '';
    cartDiscountErrorNotStackable.textContent = String(template).replace(/__CODE__/g, codeLabel);
    cartDiscountErrorNotStackable.classList.remove('hidden');
    cartDiscountError.classList.remove('hidden');
  }

  /**
   * Persists only the provided discount codes in cart without UI morph.
   * Used after rejected non-stackable attempts so refresh keeps the valid code(s).
   * @param {string[]} codes
   */
  async #persistDiscountCodes(codes) {
    if (typeof this.dataset.sectionId !== 'string') return;
    const config = fetchConfig('json', {
      body: JSON.stringify({ discount: codes.join(','), sections: [this.dataset.sectionId] }),
    });
    try {
      await fetch(Theme.routes.cart_update_url, config);
    } catch (error) {}
  }

  /**
   * Shows the replacement warning message.
   * @param {string} oldCodes - The code(s) that were replaced (comma-separated if multiple).
   * @param {string} newCode - The code that is now applied.
   * @param {number} count - Number of codes that were replaced.
   */
  #showReplacedMessage(oldCodes, newCode, count) {
    if (this.#replacedMessageHideAt) clearTimeout(this.#replacedMessageHideAt);
    this.#hideAddedMessage();
    const label = count > 1 ? 'discount codes' : 'discount code';
    const verb = count > 1 ? 'were' : 'was';
    const message = "You can't stack these discounts. The previous " + label + ' ' + oldCodes + ' ' + verb + ' replaced with ' + newCode + '.';
    const successEl = document.querySelector('[ref="cartDiscountReplaced"]') || document.querySelector('.cart-discount__success');
    const errorEl = document.querySelector('[ref="cartDiscountError"]') || document.querySelector('.cart-discount__error');
    if (successEl instanceof HTMLElement) {
      const small = successEl.querySelector('small');
      if (small) small.textContent = message;
      successEl.classList.remove('hidden');
      successEl.style.display = 'flex';
    }
    if (errorEl instanceof HTMLElement) errorEl.classList.add('hidden');
  }

  /**
   * Shows a success message.
   * @param {string} newCode - The discount code that was just stacked.
   * @param {string} [customMessage] - Optional full message to display instead of the default.
   */
  #showAddedMessage(newCode, customMessage) {
    if (this.#addedMessageHideAt) clearTimeout(this.#addedMessageHideAt);
    this.#hideReplacedMessage();
    const message = customMessage || ('An additional discount (' + newCode + ') has been added to your cart.');
    const addedEl = document.querySelector('[ref="cartDiscountAdded"]');
    const errorEl = document.querySelector('[ref="cartDiscountError"]') || document.querySelector('.cart-discount__error');
    if (addedEl instanceof HTMLElement) {
      const small = addedEl.querySelector('small');
      if (small) small.textContent = message;
      addedEl.classList.remove('hidden');
      addedEl.style.display = 'flex';
    }
    if (errorEl instanceof HTMLElement) errorEl.classList.add('hidden');
  }

  #hideAddedMessage() {
    if (this.#addedMessageHideAt) {
      clearTimeout(this.#addedMessageHideAt);
      this.#addedMessageHideAt = null;
    }
    const el = document.querySelector('[ref="cartDiscountAdded"]');
    if (el instanceof HTMLElement) {
      el.style.display = '';
      el.classList.add('hidden');
    }
  }

  #hideReplacedMessage() {
    if (this.#replacedMessageHideAt) {
      clearTimeout(this.#replacedMessageHideAt);
      this.#replacedMessageHideAt = null;
    }
    const el = document.querySelector('[ref="cartDiscountReplaced"]') || document.querySelector('.cart-discount__success');
    if (el instanceof HTMLElement) {
      el.style.display = '';
      el.classList.add('hidden');
    }
  }

  /**
   * Tries to replace existing discount(s) with the new code.
   * For a single existing non-stackable code, only replaces if the new code gives a better discount.
   * For multiple stacked codes, always replaces.
   * @param {Record<string, unknown>} currentData - Response from the initial cart update attempt.
   * @param {string} newCode - The newly entered discount code.
   * @param {string[]} existingCodes - The currently applied codes (being replaced).
   * @param {AbortController} abortController - For the request.
   * @returns {Promise<'replaced' | 'invalid' | 'not_better'>}
   */
  async #tryReplaceDiscount(currentData, newCode, existingCodes, abortController) {
    const sectionId = this.dataset.sectionId;
    if (typeof sectionId !== 'string') return 'invalid';
    const replaceConfig = fetchConfig('json', {
      body: JSON.stringify({
        discount: newCode,
        sections: [sectionId],
      }),
    });
    const replaceResponse = await fetch(Theme.routes.cart_update_url, {
      ...replaceConfig,
      signal: abortController.signal,
    });
    const replaceData = await replaceResponse.json();
    const newCodeApplicable =
      replaceData.discount_codes &&
      replaceData.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ d) => d.code === newCode && d.applicable === true);
    if (!newCodeApplicable) return 'invalid';
    if (existingCodes.length === 1) {
      const currentTotal = this.#getTotalDiscount(currentData);
      const newTotal = this.#getTotalDiscount(replaceData);
      if (currentTotal > 0 && newTotal <= currentTotal) return 'not_better';
    }
    document.dispatchEvent(new DiscountUpdateEvent(replaceData, this.id));
    morphSection(sectionId, replaceData.sections[sectionId]);
    const replacedLabel = existingCodes.join(', ');
    setTimeout(() => this.#showReplacedMessage(replacedLabel, newCode, existingCodes.length), 1500);
    console.log('[Cart discount] Replaced:', { previous: replacedLabel, new: newCode });
    return 'replaced';
  }

  /**
   * Returns total discount amount from a cart update response.
   * @param {Record<string, unknown>} data - Cart update JSON response.
   * @returns {number}
   */
  #getTotalDiscount(data) {
    const cart = /** @type {Record<string, unknown>} */ (data.cart ?? data);
    if (typeof cart.total_discount === 'number') return cart.total_discount;
    const applications = cart.discount_applications;
    if (!Array.isArray(applications)) return 0;
    return applications.reduce((sum, app) => sum + (Number(/** @type {{ total_allocated_amount?: number }} */ (app).total_allocated_amount) || 0), 0);
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
